import { Stack, Card, Typography, Divider, ButtonProps } from '@mui/material';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';

import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import Button from '../../styledComponents/Button';

interface SharedButtonProps extends ButtonProps {
  link: string;
  onClick: () => void;
}

const SharedButton = ({
  link,
  children,
  onClick,
  ...props
}: PropsWithChildren<SharedButtonProps>) => (
  <Button
    component={Link}
    to={link}
    variant="outlined"
    color="inherit"
    data-testid="managePaymentMethodsButton"
    sx={{
      width: { mobile: 1, tablet: 'auto', desktop: 1 },
      borderWidth: 2,
      fontWeight: 700,
      textTransform: 'none',
      fontSize: 18,
      color: colors.csaaBlack,
      px: { mobile: 4, desktop: 3 },
      wordWrap: 'nowrap',
      minWidth: 'max-content',
    }}
    onClick={onClick}
  >
    {children}
  </Button>
);

const TransactionHistoryCard = () => {
  const { track } = useAnalytics();
  return (
    <Card
      data-testid="TransactionHistoryCard"
      component={Stack}
      spacing={4}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: 2,
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <Stack
        spacing={3}
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
      >
        <Stack spacing={1} width={1}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: '25px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Transaction history
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '21px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaDark,
            }}
          >
            See your past bills and payments.
          </Typography>
        </Stack>
        <SharedButton
          onClick={() => {
            track('Transaction History Accessed', {
              event_type: 'Link Accessed',
              location: 'Secondary Card',
              link: 'View history',
            });
          }}
          link="/transaction-history"
        >
          View history
        </SharedButton>
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Stack
        spacing={3}
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
      >
        <Stack spacing={1} width={1}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: '25px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Manage payment methods
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '21px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaDark,
            }}
          >
            Add, update, and remove payment methods.
          </Typography>
        </Stack>
        <SharedButton
          onClick={() => {
            track('Payment Methods Accessed', {
              event_type: 'Link Accessed',
              location: 'Secondary Card',
              link: 'Manage payment methods',
            });
          }}
          link="/wallet"
        >
          Manage payment methods
        </SharedButton>
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Stack
        spacing={3}
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
      >
        <Stack spacing={1} width={1}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: 18,
              lineHeight: '25px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            AutoPay
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '21px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaDark,
            }}
          >
            Set up AutoPay. Start or stop it.
          </Typography>
        </Stack>
        <SharedButton
          onClick={() => {
            track('AutoPay Settings Accessed', {
              event_type: 'Link Accessed',
              location: 'Secondary Card',
              link: 'Manage AutoPay',
            });
          }}
          link="/autopay"
        >
          Manage AutoPay
        </SharedButton>
      </Stack>
    </Card>
  );
};

export default TransactionHistoryCard;
